import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  createCoordinator,
  getCoordinatorById,
  getCoordinatorByEmail,
  updateCoordinator,
} from "../../services/CoordinatorService";
import { SideBar } from "./inc/sideBar";
import { TopHeader } from "./inc/topHeader";
import { HSTSToast } from "../../utils/hstsToast";
import { ToastContainer } from "react-toastify";

const RegisterCoordinator = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const navigator = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("");
  const [emailAddress, setEmailAddress] = useState("");

  const [errors, setErrors] = useState({
    fullName: "",
    phoneNumber: "",
    gender: "",
    emailAddress: "",
  }); 

  const [touched, setTouched] = useState({
    fullName: false,
    phoneNumber: false,
    gender: false,
    emailAddress: false,
  });

  const [emailExists, setEmailExists] = useState(false);

  const isValidPhoneNumber = (number) => {
    const localPattern = /^(06|07)\d{8}$/;
    const intlPattern = /^\+255\s[67]\d{2}\s\d{3}\s\d{3}$/;
    return localPattern.test(number) || intlPattern.test(number);
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const formValidation = () => {
    let valid = true;
    const copy = { ...errors };

    if (!fullName.trim()) {
      copy.fullName = "Full name is required.";
      valid = false;
    } else copy.fullName = "";

    if (!phoneNumber.trim()) {
      copy.phoneNumber = "Phone number is required.";
      valid = false;
    } else if (!isValidPhoneNumber(phoneNumber.trim())) {
      copy.phoneNumber =
        "Invalid phone format. Use 0769xxxxxx or +255 769 xxx xxx.";
      valid = false;
    } else copy.phoneNumber = "";

    if (!gender.trim()) {
      copy.gender = "Gender is required.";
      valid = false;
    } else copy.gender = "";

    if (!emailAddress.trim()) {
      copy.emailAddress = "Email is required.";
      valid = false;
    } else if (!validateEmail(emailAddress)) {
      copy.emailAddress = "Invalid email address.";
      valid = false;
    } else if (emailExists && !id) {
      // check the email
      copy.emailAddress = "This email is already used.";
      valid = false;
    } else copy.emailAddress = "";

    setErrors(copy);
    return valid;
  };

  const checkEmailExists = async (email) => {
    if (!validateEmail(email)) return;
    try {
      const res = await getCoordinatorByEmail(email);
      if (res?.data) {
        setEmailExists(true);
        HSTSToast("error", "This email is already registered");
      } else {
        setEmailExists(false);
      }
    } catch (err) {
      setEmailExists(false);
    }
  };

  const saveOrUpdateCoordinator = async (e) => {
    e.preventDefault();
    setTouched({
      fullName: true,
      phoneNumber: true,
      gender: true,
      emailAddress: true,
    });

    if (formValidation()) {
      const coordinatorStatus = "enable";
      const coordinator = {
        fullName,
        phoneNumber,
        gender,
        emailAddress,
        coordinatorStatus,
      };

      if (id) {
        updateCoordinator(id, coordinator)
          .then(() => {
            HSTSToast("success", "Coordinator successfully changed");
            setTimeout(() => {
              navigator("/admin/coordinator");
            }, 1200);
          })
          .catch((err) => console.error(err));
      } else {
        if (emailExists) {
          HSTSToast("error", "This email is already registered");
          return;
        }
        createCoordinator(coordinator)
          .then(() => {
            HSTSToast("success", "Successfully coordinator registered");

            setTimeout(() => {
              navigator("/admin/coordinator");
            }, 1200);
          })
          .catch((err) => console.error(err));
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === "emailAddress" && !id) {
      checkEmailExists(emailAddress);
    }
    formValidation();
  };

  const getInputClass = (fieldName, value) => {
    if (!touched[fieldName]) return "form-control";
    if (errors[fieldName]) return "form-control is-invalid";
    if (value.trim()) return "form-control is-valid";
    return "form-control";
  };

  const pageTitle = () => (id ? "Change Coordinator" : "Register Coordinator");

  useEffect(() => {
    if (id) {
      getCoordinatorById(id)
        .then((response) => {
          const c = response.data;
          setFullName(c.fullName);
          setPhoneNumber(c.phoneNumber);
          setGender(c.gender);
          setEmailAddress(c.emailAddress);
        })
        .catch((error) => console.error("Error fetching coordinator:", error));
    }
  }, [id]);

  return (
    <>
      {/* <div className="wrapper"> */}
      <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
        <SideBar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeMenu={activeMenu}
          toggleMenu={toggleMenu}
          windowWidth={windowWidth}
        />
        <div className="main-panel">
          <TopHeader
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            windowWidth={windowWidth}
          />
          <div className="container mt-0">
            <div className="page-inner">
              <div className="page-header bg-white zis-shadow rounded-2 mt-2">
                <ul className="breadcrumbs my-md-3 mb-3 ms-0">
                  <li className="nav-home">
                    <Link to="/admin">
                      <i className="icon-home"></i>
                    </Link>
                  </li>
                  <li className="separator">
                    <i className="icon-arrow-right"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <Link to="/admin/coordinator">Manage Coordinators</Link>
                  </li>
                  <li className="separator">
                    <i className="icon-arrow-right"></i>
                  </li>
                  <li className="nav-item text-black fw-semibold">
                    <Link to="#">{pageTitle()}</Link>
                  </li>
                </ul>
              </div> 

              <div className="row mt-5 align-items-center h-100">
                <form
                  className="col-lg-4 col-12 mx-auto"
                  onSubmit={saveOrUpdateCoordinator}
                >
                  <div className="card zis-shadow rounded-3">
                    <div className="card-body">
                      <div className="text-center my-3">
                        <h1 className="h5 text-black fw-semibold">
                          {pageTitle()}
                        </h1>
                      </div>

                      <div className="form-group mb-2">
                        <label className="text-black">Full Name</label>
                        <input
                          type="text"
                          className={getInputClass("fullName", fullName)}
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          onBlur={() => handleBlur("fullName")}
                          placeholder="Enter full name"
                        />
                        {errors.fullName && (
                          <div className="invalid-feedback">
                            {errors.fullName}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-2">
                        <label className="text-black">Phone Number</label>
                        <input
                          type="tel"
                          className={getInputClass("phoneNumber", phoneNumber)}
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          onBlur={() => handleBlur("phoneNumber")}
                          placeholder="Please enter phone number"
                        />
                        {errors.phoneNumber && (
                          <div className="invalid-feedback">
                            {errors.phoneNumber}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-2">
                        <label className="text-black">Gender</label>
                        <select
                          className={
                            touched.gender
                              ? errors.gender
                                ? "form-select is-invalid"
                                : "form-select is-valid"
                              : "form-select"
                          }
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          onBlur={() => handleBlur("gender")}
                        >
                          <option value="">Select Gender</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                        </select>
                        {errors.gender && (
                          <div className="invalid-feedback">
                            {errors.gender}
                          </div>
                        )}
                      </div>
                      {!id && (
                        <>
                          <div className="form-group mb-3">
                            <label className="text-black">Email Address</label>
                            <input
                              type="email"
                              className={getInputClass(
                                "emailAddress",
                                emailAddress
                              )}
                              value={emailAddress}
                              onChange={(e) => setEmailAddress(e.target.value)}
                              onBlur={() => handleBlur("emailAddress")}
                              placeholder="Enter email"
                            />
                            {errors.emailAddress && (
                              <div className="invalid-feedback">
                                {errors.emailAddress}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      <button
                        type="submit"
                        className="btn btn-black w-100 rounded-3 mb-4"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default RegisterCoordinator;
